import { useEffect, useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { useIntl } from "react-intl";
import { useMe } from "@/auth/use-me";
import { userErrorMessage } from "@/lib/user-error";
import { PowersDataDocument } from "@/graphql/generated/operations";
import { ELEMENT_CODES } from "@/lib/elements";
import { combineLevels } from "@/lib/score-source";
import { PowerProfilePdfExportModal } from "./PowerProfilePdfExportModal";

function byAbbr<T>(rows: readonly T[] | null | undefined, element: (row: T) => unknown, value: (row: T) => number | null | undefined) {
  const levels = new Map<string, number | null | undefined>();
  for (const row of rows ?? []) {
    const code = ELEMENT_CODES[String(element(row))];
    if (code) levels.set(code, value(row));
  }
  return levels;
}

// The profile's export menu has no Levels loaded, so the export reads them itself - the same
// PowersData the Power Library shows. The dialog opens at once and fills in when they arrive.
export function PowerProfilePdfExport({
  onClose,
  onError,
}: {
  onClose: () => void;
  onError?: (message: string) => void;
}) {
  const intl = useIntl();
  const me = useMe();
  const { data, error } = useQuery(PowersDataDocument, { fetchPolicy: "network-only" });

  const levels = useMemo(() => {
    if (!data) return null;
    const self = byAbbr(data.selfAssessmentResults, (r) => r.element, (r) => r.result);
    const smart = byAbbr(data.smartAnalysisResults, (r) => r.element, (r) => r.score);
    return { self, smart, combined: combineLevels(self, smart) };
  }, [data]);

  useEffect(() => {
    if (!error) return;
    onError?.(userErrorMessage(intl, error));
    onClose();
  }, [error, intl, onClose, onError]);

  return <PowerProfilePdfExportModal onClose={onClose} levels={levels} personName={me.fullName} />;
}
